// Upload Page - Updated to match app layout
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDocuments } from '../../hooks/useDocuments';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import FileUpload from '../../components/common/FileUpload/FileUpload';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { CheckCircle, FileText, Home, List, Copy, ChevronRight, Share, Download } from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import './Upload.css';

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const { uploadNewDocument, loading, error, uploadProgress } = useDocuments();
  const [uploadedDocument, setUploadedDocument] = useState<any>(null);
  const [uploadError, setUploadError] = useState<string>('');

  const handleFileSelect = async (file: File) => {
    setUploadError('');
    setUploadedDocument(null);

    try {
      const result = await uploadNewDocument(file, (progress) => {
        console.log('Upload progress:', progress);
      });

      if (result.meta.requestStatus === 'fulfilled') {
        setUploadedDocument(result.payload);
      }
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed');
    }
  };

  const handleViewDocument = () => {
    if (uploadedDocument) {
      navigate(`${ROUTES.DOCUMENTS}/${uploadedDocument._id}`);
    }
  };

  const handleAnalyzeDocument = () => {
    if (uploadedDocument) {
      navigate(`/analysis/${uploadedDocument._id}`);
    }
  };

  const resetUpload = () => {
    setUploadedDocument(null);
    setUploadError('');
  };

  return (
    <div className="upload-container">
      {/* Top Navigation Bar - Same as other pages */}
      <div className="upload-top-nav">
        <div className="nav-left">
          {/* Navigation Icons */}
          <div className="nav-icons">
            <button 
              onClick={() => navigate('/dashboard')}
              className="nav-icon"
              title="Dashboard"
            >
              <Home size={20} />
            </button>
            <button 
              onClick={() => navigate('/documents')}
              className="nav-icon"
              title="Documents List"
            >
              <List size={20} />
            </button>
            <button 
              className="nav-icon nav-icon-current"
              title="Upload Document"
            >
              <FileText size={20} />
            </button>
            <button className="nav-icon" title="Notes">
              <Copy size={20} />
            </button>
          </div>
        </div>

        {/* Centered Title */}
        <div className="nav-center">
          <div className="header-title-section">
            <h1 className="header-title">Artifi Contract Analyzer</h1>
            <p className="header-subtitle">powered by <strong>Artifi Agentic Studio</strong></p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="nav-actions">
          <button className="action-button action-button-secondary">
            <Share size={16} />
            Share
          </button>
          <button className="action-button action-button-secondary">
            <Download size={16} />
            Download
          </button>
        </div>
      </div>

      {/* Breadcrumb Section */}
      <div className="breadcrumb-section">
        <div className="breadcrumb">
          <span className="breadcrumb-item">Dashboard</span>
          <ChevronRight size={16} className="breadcrumb-separator" />
          <span className="breadcrumb-current">Upload Document</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="upload-main">
        <div className="upload-page">
          <div className="upload-header">
            <h1>Upload Contract Document</h1>
            <p>Upload a contract document for AI-powered risk analysis</p>
          </div>

          <div className="upload-content">
            {!uploadedDocument && (
              <Card className="upload-card">
                <FileUpload
                  onFileSelect={handleFileSelect}
                  loading={loading}
                  error={uploadError || error || undefined}
                  progress={uploadProgress}
                />
                
                {loading && (
                  <div className="upload-status">
                    <LoadingSpinner size="sm" />
                    <span>Uploading and processing document...</span>
                  </div>
                )}
              </Card>
            )}

            {uploadedDocument && (
              <Card className="success-card">
                <div className="success-content">
                  <CheckCircle size={48} className="success-icon" />
                  <h2>Document Uploaded Successfully!</h2>
                  <p>Your document has been uploaded and is ready for analysis.</p>
                  
                  <div className="document-info">
                    <FileText size={20} />
                    <div>
                      <strong>{uploadedDocument.filename}</strong>
                      <br />
                      <span className="document-meta">
                        {uploadedDocument.page_count} pages • 
                        Status: {uploadedDocument.status}
                      </span>
                    </div>
                  </div>

                  <div className="success-actions">
                    <Button
                      variant="primary"
                      onClick={handleAnalyzeDocument}
                      disabled={uploadedDocument.status !== 'processed'}
                    >
                      {uploadedDocument.status === 'processed' ? 'Analyze Document' : 'Processing...'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleViewDocument}
                    >
                      View Document
                    </Button>
                    <Button
                      variant="outline"
                      onClick={resetUpload}
                    >
                      Upload Another
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Info Cards */}
            <div className="info-section">
              <Card className="info-card">
                <h3>Supported Formats</h3>
                <ul>
                  <li>PDF documents (.pdf)</li>
                  <li>Image files (.jpg, .png)</li>
                  <li>Maximum file size: 50MB</li>
                </ul>
              </Card>

              <Card className="info-card">
                <h3>What We Analyze</h3>
                <ul>
                  <li>Contract terms and clauses</li>
                  <li>Risk factors and levels</li>
                  <li>Financial obligations</li>
                  <li>Legal compliance issues</li>
                </ul>
              </Card>

              <Card className="info-card">
                <h3>Processing Time</h3>
                <ul>
                  <li>Small documents: 1-2 minutes</li>
                  <li>Large documents: 3-5 minutes</li>
                  <li>Complex contracts: 5-10 minutes</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Upload;